import { supabase } from '../config/supabase';
import { getGuestWriteTokenHeaders, setGuestWriteToken } from '../lib/guestRoomToken';
import type { RealtimeChannel } from '@supabase/supabase-js';
import type { Room, RoomOwnerType } from '../types/Room';
import type { Timer } from '../types/Timer';
import type {
  Message,
  DisplayedMessage,
  MessageFormData,
} from '../types/Message';
import type { Connection } from '../types/Connection';
import type { UserPlan, UserProfile } from '../types/User';
import type { MyTeamContext, UserTeamMembership } from '../types/Team';
import { doesConnectionUseLiveSlot } from '../lib/connectionLiveSlot';
import { normalizeConnectionLabel } from '../lib/connectionLabel';
import {
  clampLiveConnectionsToPlan,
  getMaxMessageFormsPerRoomForPlan,
  getMaxTimersPerRoomForPlan,
  getMaxTeamMembersForPlan,
} from '../lib/planLimits';
import type { PlanTier } from '../lib/planLimits';
import { TEAM_INVITE_ERROR } from '../lib/teamInvite';
import {
  deleteRoomScopedPayload,
  fetchRoomScopedPayloadMap,
  makeId,
  mergeRoomScopedPayloadAtomic,
  subscribeRoomScopedTable,
  subscribeTableRow,
  upsertRoomScopedPayload,
} from './supabaseDataAdapter';
import type { RowChangeEvent } from './supabaseDataAdapter';

// Прикладной слой данных (комнаты, таймеры, сообщения, команда) поверх Supabase.

export type { UserPlan, UserProfile } from '../types/User';

function removeUndefined<T extends Record<string, any>>(obj: T): T {
  const cleaned = { ...obj };
  for (const key in cleaned) {
    if (cleaned[key] === undefined) {
      delete cleaned[key];
    } else if (typeof cleaned[key] === 'object' && cleaned[key] !== null && !Array.isArray(cleaned[key])) {
      cleaned[key] = removeUndefined(cleaned[key]);
    }
  }
  return cleaned;
}

async function touchRoomActivity(roomId: string): Promise<void> {
  const { error } = await supabase
    .from('timer_rooms')
    .update({ updated_at: Date.now() })
    .eq('id', roomId);
  if (error) throw error;
}

// ==================== Rooms ====================

/** Если owner не передан, но сессия authenticated — подставляем auth.uid() (RLS иначе блокирует owner_id = null). */
async function resolveRoomOwnerIdForInsert(room: Omit<Room, 'id'>): Promise<string | null> {
  const raw = room.ownerId;
  if (raw != null && String(raw).trim() !== '') {
    return String(raw);
  }
  return (await getAuthUserId()) ?? null;
}

export async function createRoom(room: Omit<Room, 'id'>): Promise<string> {
  const now = Date.now();
  const owner_id = await resolveRoomOwnerIdForInsert(room);
  if (owner_id == null) {
    const { data, error } = await supabase.rpc('timer_create_guest_room', {
      p_name: room.name,
      p_settings: (room.settings ?? {}) as object,
    });
    if (error) throw error;
    const row = data as { id?: string; guest_write_token?: string } | null;
    if (!row?.id || !row.guest_write_token) {
      throw new Error('timer_create_guest_room: net response');
    }
    setGuestWriteToken(row.id, row.guest_write_token);
    return row.id;
  }
  const roomId = makeId();
  const owner_type: RoomOwnerType = room.ownerType ?? 'user';
  const payload = removeUndefined({
    id: roomId,
    name: room.name,
    settings: room.settings ?? {},
    created_at: now,
    updated_at: now,
    owner_id,
    owner_type,
    selected_timer_id: room.selectedTimerId ?? null,
  });
  const { error } = await supabase.from('timer_rooms').insert(payload);
  if (error) throw error;
  return roomId;
}

/** uid владельца тарифа для лимитов (личная комната → ownerId; командная → billingOwnerUid из RPC). */
export function resolveBillingUidFromRoom(room: Room | null | undefined): string | undefined {
  if (!room) return undefined;
  if (room.ownerType === 'team') return room.billingOwnerUid ?? undefined;
  return room.ownerId;
}

function requiresAccountForAnonAccessFromRow(row: Record<string, unknown>): boolean | undefined {
  const flagged = row.requires_account_for_anon_access;
  if (typeof flagged === 'boolean') return flagged;
  if (Object.prototype.hasOwnProperty.call(row, 'owner_id')) {
    const oid = row.owner_id;
    if (oid === null || oid === undefined) return false;
    const s = typeof oid === 'string' ? oid.trim() : String(oid).trim();
    return s.length > 0;
  }
  return undefined;
}

function roomFromRow(row: Record<string, unknown>): Room {
  const ot = (row.owner_type as RoomOwnerType | undefined) ?? 'user';
  return {
    id: String(row.id),
    name: (row.name as string) ?? '',
    settings: (row.settings as Room['settings']) ?? {},
    createdAt: (row.created_at as number) ?? 0,
    ownerId: (row.owner_id as string | undefined) ?? undefined,
    ownerType: ot,
    billingOwnerUid: (row.billing_owner_uid as string | undefined) ?? undefined,
    selectedTimerId: (row.selected_timer_id as string | undefined) ?? undefined,
    requiresAccountForAnonAccess: requiresAccountForAnonAccessFromRow(row),
  };
}

export async function listRooms(ownerId?: string): Promise<Room[]> {
  let query = supabase.from('timer_rooms').select('*');
  if (ownerId) query = query.eq('owner_type', 'user').eq('owner_id', ownerId);
  const { data, error } = await query.order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []).map((row) => roomFromRow(row as Record<string, unknown>));
}

/**
 * Командные комнаты (`owner_type = team`, `owner_id` = id команды).
 * В таблице нет `billing_owner_uid` — подставляем uid владельца команды для лимитов (как в RPC `timer_get_room_public`).
 */
export async function listRoomsForTeam(
  teamId: string,
  billingOwnerUid: string,
): Promise<Room[]> {
  const { data, error } = await supabase
    .from('timer_rooms')
    .select('*')
    .eq('owner_type', 'team')
    .eq('owner_id', teamId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  const billing = billingOwnerUid.trim();
  return (data ?? []).map((row) =>
    roomFromRow({
      ...(row as Record<string, unknown>),
      ...(billing ? { billing_owner_uid: billing } : {}),
    }),
  );
}

/** Согласовано с `listRooms`: только строки с `owner_type = user` для данного `owner_id`. */
function postgresPayloadTouchesPersonalRoom(
  userId: string,
  payload: { old?: Record<string, unknown> | null; new?: Record<string, unknown> | null },
): boolean {
  const uid = userId.trim();
  if (!uid) return false;
  for (const r of [payload.old, payload.new]) {
    if (!r || typeof r !== 'object') continue;
    if (String(r.owner_id ?? '').trim() !== uid) continue;
    const ot = r.owner_type;
    if ((ot ?? 'user') === 'user') return true;
  }
  return false;
}

/** Согласовано с `listRoomsForTeam`: только `owner_type = team` для данного `owner_id` команды. */
function postgresPayloadTouchesTeamRoom(
  teamId: string,
  payload: { old?: Record<string, unknown> | null; new?: Record<string, unknown> | null },
): boolean {
  const tid = teamId.trim();
  if (!tid) return false;
  for (const r of [payload.old, payload.new]) {
    if (!r || typeof r !== 'object') continue;
    if (String(r.owner_id ?? '').trim() !== tid) continue;
    if (r.owner_type === 'team') return true;
  }
  return false;
}

export function subscribeToPersonalRooms(userId: string, callback: () => void): () => void {
  const channel = supabase
    .channel(`timer_rooms:user:${userId}:${makeId()}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'timer_rooms',
        filter: `owner_id=eq.${userId}`,
      },
      (payload) => {
        if (!postgresPayloadTouchesPersonalRoom(userId, payload)) return;
        callback();
      },
    )
    .subscribe();
  return () => {
    void supabase.removeChannel(channel);
  };
}

export function subscribeToTeamRooms(teamId: string, callback: () => void): () => void {
  const channel = supabase
    .channel(`timer_rooms:team:${teamId}:${makeId()}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'timer_rooms',
        filter: `owner_id=eq.${teamId}`,
      },
      (payload) => {
        if (!postgresPayloadTouchesTeamRoom(teamId, payload)) return;
        callback();
      },
    )
    .subscribe();
  return () => {
    void supabase.removeChannel(channel);
  };
}

/** @deprecated Используйте subscribeToPersonalRooms; фильтр только по owner_id (личные комнаты). */
export function subscribeToRooms(ownerId: string, callback: () => void): () => void {
  return subscribeToPersonalRooms(ownerId, callback);
}

// ==================== Users ====================

/** Колонка display_name: только если ключ передан в patch (null и пустая строка после trim → очистка в БД). */
function timerUserProfileDisplayNameColumnPatch(
  profile: Partial<UserProfile>,
): { display_name: string | null } | undefined {
  if (!Object.prototype.hasOwnProperty.call(profile, 'displayName')) return undefined;
  const raw = profile.displayName;
  if (raw === undefined) return undefined;
  if (raw === null) return { display_name: null };
  if (typeof raw !== 'string') return undefined;
  const t = raw.trim();
  return { display_name: t.length ? t : null };
}

function userProfileCreatedAtMs(raw: unknown): number {
  if (raw == null) return 0;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw;
  }
  const s = String(raw).trim();
  if (/^\d+$/.test(s)) {
    const n = Number(s);
    if (Number.isFinite(n) && n > 1e11) {
      return n;
    }
  }
  const parsed = Date.parse(s);
  return Number.isFinite(parsed) ? parsed : 0;
}

export async function ensureUserProfile(
  uid: string,
  profile: Partial<UserProfile> = {},
): Promise<void> {
  const { data, error } = await supabase
    .from('timer_user_profiles')
    .select('*')
    .eq('uid', uid)
    .maybeSingle();
  if (error) throw error;

  if (!data) {
    const displayPatch = timerUserProfileDisplayNameColumnPatch(profile);
    const initialProfile = removeUndefined({
      uid,
      email: profile.email ?? null,
      plan: profile.plan ?? 'free',
      ...(displayPatch ?? {}),
    });
    const { error: insertErr } = await supabase.from('timer_user_profiles').insert(initialProfile);
    if (insertErr) throw insertErr;
    return;
  }

  if (!Object.keys(profile).length) return;
  const displayPatch = timerUserProfileDisplayNameColumnPatch(profile);
  const updates = removeUndefined({
    email: profile.email,
    plan: profile.plan,
    ...(displayPatch ?? {}),
  });
  if (Object.keys(updates).length === 0) return;
  const { error: updateErr } = await supabase
    .from('timer_user_profiles')
    .update(updates)
    .eq('uid', uid);
  if (updateErr) throw updateErr;
}

export async function getUserProfile(uid: string): Promise<UserProfile | null> {
  const { data, error } = await supabase
    .from('timer_user_profiles')
    .select('*')
    .eq('uid', uid)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  return {
    email: data.email ?? null,
    displayName: typeof data.display_name === 'string' ? data.display_name : null,
    plan: (data.plan as UserPlan | undefined) ?? 'free',
    createdAt: userProfileCreatedAtMs(data.created_at),
  };
}

/** Синхронизирует settings.maxLiveConnections с допустимым диапазоном плана владельца. */
export async function syncRoomMaxLiveConnectionsFromOwnerPlan(
  roomId: string,
  room: Room,
  ownerUid: string,
): Promise<void> {
  const profile = await getUserProfile(ownerUid);
  const plan = (profile?.plan ?? 'free') as PlanTier;
  const clamped = clampLiveConnectionsToPlan(room.settings?.maxLiveConnections, plan);
  const current = room.settings?.maxLiveConnections;
  if (current === clamped) return;
  const settings = { ...(room.settings ?? {}), maxLiveConnections: clamped };
  await updateRoom(roomId, { settings });
}

export async function getRoomStats(roomId: string): Promise<{
  timersCount: number;
  messagesCount: number;
  lastMessageTimestamp: number | null;
  lastActivityTimestamp: number | null;
  hasRunningTimer: boolean;
}> {
  const [
    { data: room, error: roomError },
    { data: timers, error: timersError },
    { data: forms, error: formsError },
    { data: currentMessage, error: currentMessageError },
  ] = await Promise.all([
    supabase.from('timer_rooms').select('*').eq('id', roomId).maybeSingle(),
    supabase.from('timer_room_timers').select('payload').eq('room_id', roomId),
    supabase.from('timer_room_message_forms').select('payload').eq('room_id', roomId),
    supabase
      .from('timer_room_current_messages')
      .select('payload')
      .eq('room_id', roomId)
      .maybeSingle(),
  ]);

  if (roomError) throw roomError;
  if (timersError) throw timersError;
  if (formsError) throw formsError;
  if (currentMessageError) throw currentMessageError;

  const timerPayloads = (timers ?? []).map((x) => (x.payload ?? {}) as Record<string, unknown>);
  const formPayloads = (forms ?? []).map((x) => (x.payload ?? {}) as Record<string, unknown>);

  const timersCount = timerPayloads.length;
  const hasRunningTimer = timerPayloads.some((x) => x.status === 'running');
  const messagesCount = formPayloads.filter((x) => typeof x.text === 'string' && x.text.trim()).length;
  const lastMessageTimestamp =
    currentMessage?.payload &&
    typeof (currentMessage.payload as Record<string, unknown>).timestamp === 'number'
      ? Number((currentMessage.payload as Record<string, unknown>).timestamp)
      : null;
  const lastActivityTimestamp =
    typeof room?.updated_at === 'number'
      ? room.updated_at
      : typeof room?.created_at === 'number'
        ? room.created_at
        : lastMessageTimestamp;

  return {
    timersCount,
    messagesCount,
    lastMessageTimestamp,
    lastActivityTimestamp,
    hasRunningTimer,
  };
}

export async function deleteRoom(roomId: string): Promise<void> {
  const { error } = await supabase.from('timer_rooms').delete().eq('id', roomId);
  if (error) throw error;
}

let isGetRoomPublicRpcAvailable: boolean | null = null;

/** Пока неизвестно, есть ли `timer_get_room_public` в PostgREST — сериализуем первый «пробный» вызов, иначе 4× 404 в сети. */
let getRoomRpcUnknownChain: Promise<void> = Promise.resolve();

function isRpcUnavailableError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const o = error as { code?: unknown; message?: unknown; details?: unknown };
  const code = String(o.code ?? '');
  const message = String(o.message ?? '');
  const details = String(o.details ?? '');
  return (
    code === 'PGRST202' ||
    code === '42883' ||
    code === '404' ||
    /schema cache|could not find|function public\.timer_get_room_public|404/i.test(message) ||
    /404|not find/i.test(details)
  );
}

function isPermissionDeniedError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const code = 'code' in error ? String((error as { code?: unknown }).code ?? '') : '';
  const message = 'message' in error ? String((error as { message?: unknown }).message ?? '') : '';
  return code === '42501' || /permission denied|not allowed/i.test(message);
}

/**
 * Без RPC `timer_get_room_public`: billing_owner_uid для командной комнаты.
 * Владелец — строка `timer_teams`; участник — своя строка `timer_user_team_membership` (RLS).
 */
async function fetchBillingOwnerUidForTeamRoom(teamId: string): Promise<string | undefined> {
  const tid = teamId.trim();
  if (!tid) return undefined;

  const { data: teamRow } = await supabase
    .from('timer_teams')
    .select('owner_uid')
    .eq('id', tid)
    .maybeSingle();
  const owned = teamRow?.owner_uid;
  if (owned != null && String(owned).trim() !== '') return String(owned).trim();

  const { data: sessionData } = await supabase.auth.getSession();
  const authUid = sessionData.session?.user?.id?.trim();
  if (!authUid) return undefined;

  const { data: memRow } = await supabase
    .from('timer_user_team_membership')
    .select('owner_uid')
    .eq('team_id', tid)
    .eq('uid', authUid)
    .maybeSingle();
  const fromMem = memRow?.owner_uid;
  if (fromMem != null && String(fromMem).trim() !== '') return String(fromMem).trim();

  return undefined;
}

async function getRoomDirect(roomId: string): Promise<Room | null> {
  const { data: row, error: rowError } = await supabase
    .from('timer_rooms')
    .select('*')
    .eq('id', roomId)
    .maybeSingle();
  if (rowError) throw rowError;
  if (!row) return null;

  const rec = row as Record<string, unknown>;
  const ot = (rec.owner_type as RoomOwnerType | undefined) ?? 'user';
  if (ot === 'team') {
    const teamId = String(rec.owner_id ?? '').trim();
    const billing = teamId ? await fetchBillingOwnerUidForTeamRoom(teamId) : undefined;
    return roomFromRow({
      ...rec,
      ...(billing ? { billing_owner_uid: billing } : {}),
    });
  }
  return roomFromRow(rec);
}

export async function getRoom(roomId: string): Promise<Room | null> {
  if (isGetRoomPublicRpcAvailable === false) {
    return getRoomDirect(roomId);
  }

  if (isGetRoomPublicRpcAvailable === true) {
    const { data, error } = await supabase.rpc('timer_get_room_public', { p_room_id: roomId });
    if (!error) {
      if (data == null) return null;
      return roomFromRow(data as Record<string, unknown>);
    }
    if (isRpcUnavailableError(error)) {
      isGetRoomPublicRpcAvailable = null;
    } else {
      throw error;
    }
  }

  if (isGetRoomPublicRpcAvailable === null) {
    const run = getRoomRpcUnknownChain.then(() => getRoomWhenRpcAvailabilityUnknown(roomId));
    getRoomRpcUnknownChain = run.then(
      () => undefined,
      () => undefined,
    );
    return run;
  }

  // Недостижимо: isGetRoomPublicRpcAvailable: boolean | null
  throw new Error('getRoom: unexpected rpc availability state');
}

async function getRoomWhenRpcAvailabilityUnknown(roomId: string): Promise<Room | null> {
  if (isGetRoomPublicRpcAvailable === false) {
    return getRoomDirect(roomId);
  }
  if (isGetRoomPublicRpcAvailable === true) {
    const { data, error } = await supabase.rpc('timer_get_room_public', { p_room_id: roomId });
    if (error) throw error;
    if (data == null) return null;
    return roomFromRow(data as Record<string, unknown>);
  }

  const { data, error } = await supabase.rpc('timer_get_room_public', { p_room_id: roomId });
  if (!error) {
    isGetRoomPublicRpcAvailable = true;
    if (data == null) return null;
    return roomFromRow(data as Record<string, unknown>);
  }

  if (isRpcUnavailableError(error)) {
    try {
      const room = await getRoomDirect(roomId);
      isGetRoomPublicRpcAvailable = false;
      return room;
    } catch (directError) {
      if (isPermissionDeniedError(directError)) {
        isGetRoomPublicRpcAvailable = null;
        return null;
      }
      throw directError;
    }
  }

  throw error;
}

/** Комната меняется редко; без postgres realtime по строке (RLS для anon). */
const ROOM_METADATA_POLL_MS = 3000;

/** Таймеры: страховка если Realtime не шлёт события (self-hosted, публикация, сеть, anon). */
const TIMER_PAYLOAD_POLL_MS = 1000;

export type SubscribeToRoomOptions = {
  /** Если данные уже загружены через getRoomBootstrap — не дублировать первый fetch. */
  skipInitialRefresh?: boolean;
};

export function subscribeToRoom(
  roomId: string,
  callback: (room: Room | null) => void,
  options?: SubscribeToRoomOptions,
): () => void {
  const refresh = async () => {
    try {
      callback(await getRoom(roomId));
    } catch (e) {
      console.error(e);
      // Транзиентные ошибки RPC/сети не должны выкидывать пользователя с /room/:id.
      // Возврат null здесь приводил к ложному редиректу на главную.
    }
  };
  if (!options?.skipInitialRefresh) {
    void refresh();
  }
  const pollId = window.setInterval(() => void refresh(), ROOM_METADATA_POLL_MS);
  return () => clearInterval(pollId);
}

export async function updateRoom(roomId: string, updates: Partial<Room>): Promise<void> {
  const payload = removeUndefined({
    name: updates.name,
    settings: updates.settings,
    selected_timer_id: updates.selectedTimerId,
    owner_id: updates.ownerId,
    owner_type: updates.ownerType,
  });
  const { error } = await supabase.from('timer_rooms').update(payload).eq('id', roomId);
  if (error) throw error;
  await touchRoomActivity(roomId);
}

/** Личная ↔ командная комната (RPC, лимиты и RLS на сервере). */
export async function setRoomOwnerScopeViaRpc(
  roomId: string,
  targetScope: RoomOwnerType,
): Promise<void> {
  const scope = targetScope === 'team' ? 'team' : 'user';
  const { error } = await supabase.rpc('timer_room_set_owner_scope', {
    p_room_id: roomId,
    p_target_scope: scope,
  });
  if (error) throw error;
}


// ==================== Timers ====================

export async function createTimer(roomId: string, timer: Omit<Timer, 'id'>): Promise<string> {
  let plan: PlanTier = 'free';
  try {
    const roomData = await getRoom(roomId);
    const billingUid = resolveBillingUidFromRoom(roomData);
    if (billingUid) {
      plan = (((await getUserProfile(billingUid))?.plan ?? 'free') as PlanTier);
    }
  } catch (error) {
    // Создание таймера не должно блокироваться из-за временной недоступности чтения комнаты.
    console.warn('Не удалось определить план владельца, используем free:', error);
  }
  const { count, error: countErr } = await supabase
    .from('timer_room_timers')
    .select('*', { count: 'exact', head: true })
    .eq('room_id', roomId);
  if (countErr) throw countErr;
  const maxT = getMaxTimersPerRoomForPlan(plan);
  if ((count ?? 0) >= maxT) {
    const err = new Error('LIMIT_TIMERS');
    (err as Error & { code?: string }).code = 'LIMIT_TIMERS';
    throw err;
  }
  const timerId = makeId();
  const cleanedTimer = removeUndefined(timer);
  await upsertRoomScopedPayload('timer_room_timers', roomId, timerId, cleanedTimer);
  await touchRoomActivity(roomId);
  return timerId;
}

const DEFAULT_TIMER_ID = '__default_timer__';

export async function ensureDefaultTimer(roomId: string): Promise<string | null> {
  const { count, error: countErr } = await supabase
    .from('timer_room_timers')
    .select('*', { count: 'exact', head: true })
    .eq('room_id', roomId);
  if (countErr) throw countErr;
  if ((count ?? 0) > 0) return null;

  const defaultTimer = {
    title: 'Таймер 1',
    duration: 600,
    type: 'countdown',
    status: 'idle',
    currentTime: 600,
    wrapUpTimes: {
      yellow: 180,
      red: 60,
    },
  } satisfies Omit<Timer, 'id'>;

  await upsertRoomScopedPayload('timer_room_timers', roomId, DEFAULT_TIMER_ID, defaultTimer);
  await touchRoomActivity(roomId);
  return DEFAULT_TIMER_ID;
}

export async function getTimer(roomId: string, timerId: string): Promise<Timer | null> {
  const { data, error } = await supabase
    .from('timer_room_timers')
    .select('payload')
    .eq('room_id', roomId)
    .eq('timer_id', timerId)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  return { id: timerId, ...(data.payload as Omit<Timer, 'id'>) };
}

export function subscribeToTimer(
  roomId: string,
  timerId: string,
  callback: (timer: Timer | null) => void
): () => void {
  const refresh = async () => {
    try {
      callback(await getTimer(roomId, timerId));
    } catch (e) {
      console.error(e);
      callback(null);
    }
  };
  void refresh();
  const unsubRow = subscribeTableRow('timer_room_timers', 'timer_id', timerId, () => void refresh());
  const unsubRoom = subscribeRoomScopedTable('timer_room_timers', roomId, () => void refresh());
  return () => {
    unsubRow();
    unsubRoom();
  };
}

export async function fetchTimersForRoom(roomId: string): Promise<Record<string, Timer>> {
  const map = await fetchRoomScopedPayloadMap('timer_room_timers', roomId);
  const timers: Record<string, Timer> = {};
  for (const [id, payload] of Object.entries(map)) {
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
      console.warn('[timers] пропущена некорректная запись', { roomId, timerId: id, payload });
      continue;
    }
    const raw = payload as Omit<Timer, 'id'> & { hardStartAt?: number | null };
    const hardStartAt =
      raw.hardStartAt != null && Number.isFinite(raw.hardStartAt) ? raw.hardStartAt : undefined;
    timers[id] = { id, ...raw, hardStartAt };
  }
  return timers;
}

export type RoomBootstrap = {
  room: Room | null;
  timers: Record<string, Timer>;
};

/** Один параллельный старт: метаданные комнаты + таймеры (меньше гонок и дублей запросов). */
export async function getRoomBootstrap(roomId: string): Promise<RoomBootstrap> {
  const [room, timers] = await Promise.all([
    getRoom(roomId),
    fetchTimersForRoom(roomId).catch((e) => {
      console.error('Ошибка загрузки таймеров (bootstrap), открываем комнату с пустым списком:', e);
      return {} as Record<string, Timer>;
    }),
  ]);
  return { room, timers };
}

export type SubscribeToTimersOptions = {
  skipInitialRefresh?: boolean;
  /**
   * Периодический fetch (см. TIMER_PAYLOAD_POLL_MS). Включать только для viewer:
   * на пульте оптимистичные обновления иначе перезаписываются устаревшим снимком.
   */
  pollFallback?: boolean;
};

export function subscribeToTimers(
  roomId: string,
  callback: (timers: Record<string, Timer>) => void,
  options?: SubscribeToTimersOptions,
): () => void {
  const refresh = async () => {
    try {
      callback(await fetchTimersForRoom(roomId));
    } catch (e) {
      console.error(e);
      callback({});
    }
  };
  if (!options?.skipInitialRefresh) {
    void refresh();
  }
  // Первый fetch может быть раньше, чем фоновое создание дефолтного таймера; Realtime иногда приходит с задержкой.
  const retryTimeouts = [120, 450, 1200].map((ms) =>
    setTimeout(() => {
      void refresh();
    }, ms),
  );
  const unsub = subscribeRoomScopedTable('timer_room_timers', roomId, () => void refresh());
  const pollId =
    options?.pollFallback && typeof window !== 'undefined'
      ? window.setInterval(() => void refresh(), TIMER_PAYLOAD_POLL_MS)
      : null;
  return () => {
    retryTimeouts.forEach((id) => clearTimeout(id));
    if (pollId !== null) window.clearInterval(pollId);
    unsub();
  };
}

export async function updateTimer(
  roomId: string,
  timerId: string,
  updates: Partial<Timer>
): Promise<void> {
  const cleanedUpdates = removeUndefined(updates as Record<string, unknown>);
  delete cleanedUpdates.id;
  if (!Object.keys(cleanedUpdates).length) return;
  await mergeRoomScopedPayloadAtomic('timer_room_timers', roomId, timerId, cleanedUpdates);
  await touchRoomActivity(roomId);
}

export async function deleteTimer(roomId: string, timerId: string): Promise<void> {
  await deleteRoomScopedPayload('timer_room_timers', roomId, timerId);
  await touchRoomActivity(roomId);
}

// ==================== Messages ====================

export async function createMessage(
  roomId: string,
  message: Omit<Message, 'id' | 'timestamp'>
): Promise<string> {
  const messageId = makeId();
  const payload = removeUndefined({
    ...message,
    timestamp: Date.now(),
  });
  await upsertRoomScopedPayload('timer_room_messages', roomId, messageId, payload);
  await touchRoomActivity(roomId);
  return messageId;
}

export function subscribeToMessages(
  roomId: string,
  callback: (messages: Record<string, Message>) => void
): () => void {
  const refresh = async () => {
    try {
      const map = await fetchRoomScopedPayloadMap('timer_room_messages', roomId);
      const messages: Record<string, Message> = {};
      for (const [id, payload] of Object.entries(map)) {
        messages[id] = { id, ...(payload as Omit<Message, 'id'>) };
      }
      callback(messages);
    } catch (e) {
      console.error(e);
      callback({});
    }
  };
  void refresh();
  return subscribeRoomScopedTable('timer_room_messages', roomId, () => void refresh());
}

export async function deleteMessage(roomId: string, messageId: string): Promise<void> {
  await deleteRoomScopedPayload('timer_room_messages', roomId, messageId);
  await touchRoomActivity(roomId);
}

// ==================== Current message (show/hide on viewer) ====================

export async function setCurrentMessage(
  roomId: string,
  payload: DisplayedMessage | null
): Promise<void> {
  const table = supabase.from('timer_room_current_messages');
  if (payload === null) {
    const { error } = await table.delete().eq('room_id', roomId);
    if (error) throw error;
    await touchRoomActivity(roomId);
    return;
  }
  const cleaned = removeUndefined({
    ...payload,
    // Используем отдельное поле timestamp, чтобы можно было
    // считать "последние изменения" комнаты по сообщениям.
    timestamp: Date.now(),
  });
  const { error } = await table.upsert(
    { room_id: roomId, payload: cleaned },
    { onConflict: 'room_id' },
  );
  if (error) throw error;
  await touchRoomActivity(roomId);
}

export function subscribeToCurrentMessage(
  roomId: string,
  callback: (message: DisplayedMessage | null) => void
): () => void {
  const refresh = async () => {
    const { data, error } = await supabase
      .from('timer_room_current_messages')
      .select('payload')
      .eq('room_id', roomId)
      .maybeSingle();
    if (error) {
      console.error(error);
      callback(null);
      return;
    }
    callback(data?.payload ? (data.payload as DisplayedMessage) : null);
  };
  void refresh();
  return subscribeTableRow('timer_room_current_messages', 'room_id', roomId, () => void refresh());
}

// ==================== Message forms (synced list) ====================

export type { MessageFormData } from '../types/Message';

export async function addMessageForm(roomId: string): Promise<string> {
  const roomData = await getRoom(roomId);
  const billingUid = resolveBillingUidFromRoom(roomData);
  const plan = (billingUid ? (await getUserProfile(billingUid))?.plan ?? 'free' : 'free') as PlanTier;
  const map = await fetchRoomScopedPayloadMap('timer_room_message_forms', roomId);
  const formCount = Object.keys(map).length;
  let maxOrder = -1;
  for (const data of Object.values(map)) {
    if (typeof data?.order === 'number' && data.order > maxOrder) maxOrder = data.order;
  }
  const maxForms = getMaxMessageFormsPerRoomForPlan(plan);
  if (formCount >= maxForms) {
    const err = new Error('LIMIT_MESSAGE_FORMS');
    (err as Error & { code?: string }).code = 'LIMIT_MESSAGE_FORMS';
    throw err;
  }
  const formId = makeId();
  await upsertRoomScopedPayload('timer_room_message_forms', roomId, formId, {
    text: '',
    color: 'white',
    isBlinking: false,
    bold: false,
    order: maxOrder + 1,
  });
  await touchRoomActivity(roomId);
  return formId;
}

export function subscribeToMessageForms(
  roomId: string,
  callback: (forms: Record<string, MessageFormData>) => void
): () => void {
  const refresh = async () => {
    try {
      const map = await fetchRoomScopedPayloadMap('timer_room_message_forms', roomId);
      const forms: Record<string, MessageFormData> = {};
      for (const [id, payload] of Object.entries(map)) {
        forms[id] = payload as unknown as MessageFormData;
      }
      callback(forms);
    } catch (e) {
      console.error(e);
      callback({});
    }
  };
  void refresh();
  return subscribeRoomScopedTable('timer_room_message_forms', roomId, () => void refresh());
}

export async function updateMessageForm(
  roomId: string,
  formId: string,
  updates: Partial<MessageFormData>
): Promise<void> {
  const cleaned = removeUndefined(updates as Record<string, unknown>);
  if (!Object.keys(cleaned).length) return;
  await mergeRoomScopedPayloadAtomic('timer_room_message_forms', roomId, formId, cleaned);
  await touchRoomActivity(roomId);
}

export async function removeMessageForm(roomId: string, formId: string): Promise<void> {
  await deleteRoomScopedPayload('timer_room_message_forms', roomId, formId);
  await touchRoomActivity(roomId);
}

// ==================== Connections ====================

/** Дольше этого без heartbeat считаем узел «сиротой» и удаляем (см. useLiveConnection HEARTBEAT_MS ~28s). */
const STALE_CONNECTION_MS = 180_000;
/** Страховочный опрос списка (чистка фантомов); правки строк приходят через Realtime. Меньше STALE_CONNECTION_MS. */
const CONNECTIONS_REFRESH_POLL_MS = 60_000;
const CONNECTION_PRESENCE_PREFIX = 'timer_room_connections_presence';
const PRESENCE_OBSERVER_TIMEOUT_MS = 1_600;
const PRESENCE_SNAPSHOT_CACHE_MS = 1_200;

interface PresenceChannelState {
  channel: RealtimeChannel;
}

const liveConnectionPresenceChannels = new Map<string, PresenceChannelState>();
const presenceSnapshotCache = new Map<
  string,
  {
    expiresAt: number;
    keys: Set<string>;
    pending: Promise<Set<string>> | null;
  }
>();

function liveConnectionPresenceMapKey(roomId: string, connectionId: string): string {
  return `${roomId}:${connectionId}`;
}

function parsePresenceStateKeys(state: Record<string, unknown>): Set<string> {
  const keys = new Set<string>();
  for (const [presenceKey, metasRaw] of Object.entries(state)) {
    if (!presenceKey) continue;
    const metas = Array.isArray(metasRaw)
      ? metasRaw
      : metasRaw && typeof metasRaw === 'object' && Array.isArray((metasRaw as { metas?: unknown[] }).metas)
        ? (metasRaw as { metas: unknown[] }).metas
        : [];
    if (metas.length > 0) {
      keys.add(presenceKey);
    }
  }
  return keys;
}

async function subscribePresenceChannel(channel: RealtimeChannel): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const timeoutId = setTimeout(() => reject(new Error('Presence subscribe timeout')), 3_000);
    channel.subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        clearTimeout(timeoutId);
        resolve();
      } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
        clearTimeout(timeoutId);
        reject(new Error(`Presence channel status: ${status}`));
      }
    });
  });
}

async function trackConnectionPresence(roomId: string, connectionId: string): Promise<void> {
  const mapKey = liveConnectionPresenceMapKey(roomId, connectionId);
  if (liveConnectionPresenceChannels.has(mapKey)) return;
  const channel = supabase.channel(`${CONNECTION_PRESENCE_PREFIX}:${roomId}`, {
    config: { presence: { key: connectionId } },
  });
  try {
    await subscribePresenceChannel(channel);
    const trackResult = await channel.track({ connectionId, roomId, onlineAt: Date.now() });
    if (trackResult !== 'ok') {
      throw new Error(`Presence track failed: ${trackResult}`);
    }
    liveConnectionPresenceChannels.set(mapKey, { channel });
  } catch (e) {
    await channel.unsubscribe().catch(() => {});
    void supabase.removeChannel(channel);
    throw e;
  }
}

async function untrackConnectionPresence(roomId: string, connectionId: string): Promise<void> {
  const mapKey = liveConnectionPresenceMapKey(roomId, connectionId);
  const tracked = liveConnectionPresenceChannels.get(mapKey);
  if (!tracked) return;
  liveConnectionPresenceChannels.delete(mapKey);
  try {
    await tracked.channel.untrack();
  } catch {
    // ignore: channel может быть уже закрыт браузером при unload
  }
  await tracked.channel.unsubscribe().catch(() => {});
  void supabase.removeChannel(tracked.channel);
}

async function fetchPresenceConnectionIds(roomId: string): Promise<Set<string>> {
  const now = Date.now();
  const cached = presenceSnapshotCache.get(roomId);
  if (cached) {
    if (cached.pending) {
      const keys = await cached.pending;
      return new Set(keys);
    }
    if (cached.expiresAt > now) {
      return new Set(cached.keys);
    }
  }

  const pending = (async () => {
  const observerKey = `observer-${makeId()}`;
  const observerChannel = supabase.channel(`${CONNECTION_PRESENCE_PREFIX}:${roomId}`, {
    config: { presence: { key: observerKey } },
  });
  try {
    await subscribePresenceChannel(observerChannel);
    const tracked = await observerChannel.track({ observer: true, at: Date.now() });
    if (tracked !== 'ok') {
      throw new Error(`Presence observer track failed: ${tracked}`);
    }
    await new Promise((resolve) => setTimeout(resolve, PRESENCE_OBSERVER_TIMEOUT_MS));
    const rawState = observerChannel.presenceState();
    const keys = parsePresenceStateKeys(rawState as Record<string, unknown>);
    keys.delete(observerKey);
    presenceSnapshotCache.set(roomId, {
      expiresAt: Date.now() + PRESENCE_SNAPSHOT_CACHE_MS,
      keys: new Set(keys),
      pending: null,
    });
    return keys;
  } finally {
    await observerChannel.untrack().catch(() => {});
    await observerChannel.unsubscribe().catch(() => {});
    void supabase.removeChannel(observerChannel);
  }
  })();

  presenceSnapshotCache.set(roomId, {
    expiresAt: now + PRESENCE_SNAPSHOT_CACHE_MS,
    keys: cached?.keys ?? new Set<string>(),
    pending,
  });
  try {
    const keys = await pending;
    return new Set(keys);
  } catch (error) {
    if (cached && cached.expiresAt > now) {
      return new Set(cached.keys);
    }
    presenceSnapshotCache.delete(roomId);
    throw error;
  }
}

export type LiveConnectionPayload = Omit<Connection, 'id' | 'lastSeen' | 'heartbeatSecret'>;

export interface RegisteredLiveConnection {
  connectionId: string;
  heartbeatSecret: string;
}

export {
  MAX_CONNECTION_LABEL_LENGTH,
  normalizeConnectionLabel,
} from '../lib/connectionLabel';

export type LiveConnectionRegisterErrorCode =
  | 'LIMIT_REACHED'
  | 'NOT_ALLOWED'
  | 'REGISTER_FAILED';

export class LiveConnectionRegisterError extends Error {
  code: LiveConnectionRegisterErrorCode;

  constructor(code: LiveConnectionRegisterErrorCode, message: string) {
    super(message);
    this.name = 'LiveConnectionRegisterError';
    this.code = code;
  }
}

let isAtomicConnectionRegisterRpcAvailable: boolean | null = null;

async function getAuthUserId(): Promise<string | null> {
  try {
    const authClient = (supabase as unknown as {
      auth?: { getUser?: () => Promise<{ data?: { user?: { id?: string } } }> };
    }).auth;
    if (!authClient?.getUser) return null;
    const res = await authClient.getUser();
    return res?.data?.user?.id ?? null;
  } catch {
    return null;
  }
}

/** Fallback: прямой PostgREST insert (обходит сбои supabase-js insert в части окружений). */
async function insertTimerRoomConnectionViaRest(
  roomId: string,
  connectionId: string,
  payload: Record<string, unknown>,
): Promise<void> {
  const base = import.meta.env.VITE_SUPABASE_URL?.trim()?.replace(/\/$/, '');
  const anon = import.meta.env.VITE_SUPABASE_ANON_KEY?.trim();
  if (!base || !anon) {
    throw new Error('Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY');
  }
  const url = `${base}/rest/v1/timer_room_connections`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      apikey: anon,
      Authorization: `Bearer ${anon}`,
      'Content-Type': 'application/json',
      Prefer: 'return=minimal',
      ...getGuestWriteTokenHeaders(roomId),
    },
    body: JSON.stringify([{ room_id: roomId, connection_id: connectionId, payload }]),
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`REST insert timer_room_connections failed: ${res.status} ${detail}`);
  }
}

async function insertTimerRoomConnectionWithAtomicSlotLimit(
  roomId: string,
  connectionId: string,
  payload: Record<string, unknown>,
  maxLiveConnections: number,
): Promise<'inserted' | 'limit_reached' | 'rpc_unavailable'> {
  if (isAtomicConnectionRegisterRpcAvailable === false) {
    return 'rpc_unavailable';
  }
  const { data, error } = await supabase.rpc('timer_register_live_connection', {
    p_room_id: roomId,
    p_connection_id: connectionId,
    p_payload: payload,
    p_max_live_connections: maxLiveConnections,
    p_stale_connection_ms: STALE_CONNECTION_MS,
  });
  if (error) {
    if (isRpcUnavailableError(error)) {
      isAtomicConnectionRegisterRpcAvailable = false;
      return 'rpc_unavailable';
    }
    if (isPermissionDeniedError(error)) {
      throw new LiveConnectionRegisterError('NOT_ALLOWED', 'not allowed to register connection');
    }
    throw new LiveConnectionRegisterError(
      'REGISTER_FAILED',
      `atomic connection register failed: ${String((error as { message?: string }).message ?? 'unknown')}`,
    );
  }

  isAtomicConnectionRegisterRpcAvailable = true;
  const row = (data ?? null) as { inserted?: boolean; reason?: string } | null;
  if (row?.inserted === true) {
    return 'inserted';
  }
  if (row?.reason === 'limit_reached') {
    return 'limit_reached';
  }
  throw new LiveConnectionRegisterError(
    'REGISTER_FAILED',
    'atomic connection register returned unexpected result',
  );
}

function connectionLastSeenMs(v: { lastSeen?: unknown }): number | null {
  const raw = v.lastSeen;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string' && raw.trim() !== '') {
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function connectionFromRaw(id: string, raw: unknown): Connection | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const v = raw as Partial<Connection>;
  const { heartbeatSecret: _omitHs, id: _payloadId, ...rest } = v as Connection;
  return {
    ...rest,
    id,
    type: (rest.type as Connection['type']) ?? 'room',
    lastSeen: connectionLastSeenMs(v) ?? Date.now(),
  };
}

/** Payload строки timer_room_connections из события Realtime (UPDATE/INSERT), если он пришёл целиком. */
function connectionRowFromEvent(
  event: RowChangeEvent | undefined,
): { id: string; payload: Record<string, unknown> } | null {
  const row = event?.newRow;
  if (!row) return null;
  const id = typeof row.connection_id === 'string' ? row.connection_id : '';
  const payload = row.payload;
  if (!id || !payload || typeof payload !== 'object' || Array.isArray(payload)) return null;
  return { id, payload: payload as Record<string, unknown> };
}

/**
 * Активные подключения для UI и лимита слотов.
 * Снимок Realtime presence используется только для защиты от ложного удаления presence-tracked
 * строк при устаревшем heartbeat; при недоступности presence — только lastSeen (STALE_CONNECTION_MS).
 * Узлы без lastSeen / с протухшим heartbeat удаляются как «фантомы».
 */
async function getActiveConnectionsMap(
  roomId: string,
  map: Record<string, unknown>,
): Promise<Record<string, Connection>> {
  const now = Date.now();
  let presenceKeys: Set<string> | null = null;
  try {
    presenceKeys = await fetchPresenceConnectionIds(roomId);
  } catch {
    // Presence может быть временно недоступен (сеть/канал) — в этом случае
    // используем только heartbeat-based очистку по lastSeen.
    presenceKeys = null;
  }

  const active: Record<string, Connection> = {};
  const staleRemovals: Promise<unknown>[] = [];

  for (const [id, raw] of Object.entries(map)) {
    if (raw == null || typeof raw !== 'object') continue;
    const v = raw as Partial<Connection>;
    const lastSeenMs = connectionLastSeenMs(v);
    const isPresenceTracked = v?.presenceTracked === true;
    const isHeartbeatStale =
      lastSeenMs !== null ? now - lastSeenMs > STALE_CONNECTION_MS : false;
    const missingFromPresence =
      isPresenceTracked && presenceKeys !== null && !presenceKeys.has(id);
    const shouldRemoveMissingPresence = missingFromPresence && isHeartbeatStale;
    const shouldRemoveByHeartbeatOnly = !isPresenceTracked && isHeartbeatStale;

    if (shouldRemoveByHeartbeatOnly || shouldRemoveMissingPresence) {
      staleRemovals.push(
        deleteRoomScopedPayload('timer_room_connections', roomId, id).catch(() => {
          // У viewer может не быть прав на delete; это не должно ломать расчёт активных подключений.
        }),
      );
      continue;
    }

    // id строки = connection_id (ключ map); в payload нельзя перезаписывать его из rest.
    // Без type панель не попадала в секции LiveConnectionsPanel (только room|moderator|viewer|agenda).
    const conn = connectionFromRaw(id, v);
    if (conn) active[id] = conn;
  }

  if (staleRemovals.length > 0) {
    await Promise.all(staleRemovals);
  }

  return active;
}
/**
 * Число активных подключений. Узлы с lastSeen старше STALE_CONNECTION_MS удаляются из БД —
 * иначе после сбоев onDisconnect остаются «фантомы» и блокируют вход при пустых вкладках.
 */
export async function getConnectionCount(roomId: string): Promise<number> {
  const map = await fetchRoomScopedPayloadMap('timer_room_connections', roomId);
  const active = await getActiveConnectionsMap(roomId, map);
  return Object.keys(active).length;
}

export { doesConnectionUseLiveSlot } from '../lib/connectionLiveSlot';

function connectionConsumesLiveSlot(c: Connection): boolean {
  return doesConnectionUseLiveSlot(c);
}

/** Число активных подключений, расходующих слот лицензии (для проверки до registerLiveConnection). */
export async function getConnectionCountForSlotLimit(roomId: string): Promise<number> {
  const map = await fetchRoomScopedPayloadMap('timer_room_connections', roomId);
  const active = await getActiveConnectionsMap(roomId, map);
  let n = 0;
  for (const c of Object.values(active)) {
    if (connectionConsumesLiveSlot(c)) n += 1;
  }
  return n;
}

/** Регистрирует вкладку/окно; при обрыве соединения узел удалится (onDisconnect). */
export async function registerLiveConnection(
  roomId: string,
  payload: LiveConnectionPayload,
  options?: { maxLiveConnections?: number },
): Promise<RegisteredLiveConnection> {
  const connectionId = makeId();
  const heartbeatSecret = makeId();
  const ownerUserId = await getAuthUserId();
  let presenceTracked = true;
  try {
    await trackConnectionPresence(roomId, connectionId);
  } catch (e) {
    // Fallback: при проблемах realtime/presence всё равно регистрируем вкладку,
    // чтобы слот считался активным по heartbeat (lastSeen).
    presenceTracked = false;
    console.warn('[connections] presence track failed, continue without presence', e);
  }
  const data = removeUndefined({
    ...payload,
    ownerUserId,
    heartbeatSecret,
    presenceTracked,
    lastSeen: Date.now(),
  } as Record<string, unknown>) as Omit<Connection, 'id'>;
  try {
    const maxLiveConnections = options?.maxLiveConnections;
    if (typeof maxLiveConnections === 'number' && maxLiveConnections > 0) {
      const atomicResult = await insertTimerRoomConnectionWithAtomicSlotLimit(
        roomId,
        connectionId,
        data as Record<string, unknown>,
        maxLiveConnections,
      );
      if (atomicResult === 'limit_reached') {
        throw new LiveConnectionRegisterError('LIMIT_REACHED', 'live connection slot limit reached');
      }
      if (atomicResult === 'inserted') {
        return { connectionId, heartbeatSecret };
      }
    }
    const { error } = await supabase
      .from('timer_room_connections')
      .insert({ room_id: roomId, connection_id: connectionId, payload: data });
    if (error) {
      if (isPermissionDeniedError(error)) {
        throw new LiveConnectionRegisterError('NOT_ALLOWED', 'not allowed to register connection');
      }
      throw error;
    }
    return { connectionId, heartbeatSecret };
  } catch (e) {
    if (
      e instanceof LiveConnectionRegisterError &&
      (e.code === 'LIMIT_REACHED' || e.code === 'NOT_ALLOWED')
    ) {
      if (presenceTracked) {
        await untrackConnectionPresence(roomId, connectionId);
      }
      throw e;
    }
    try {
      await insertTimerRoomConnectionViaRest(roomId, connectionId, data as Record<string, unknown>);
      return { connectionId, heartbeatSecret };
    } catch (restErr) {
      console.warn('[connections] supabase insert failed, REST fallback failed', e, restErr);
      if (presenceTracked) {
        await untrackConnectionPresence(roomId, connectionId);
      }
      if (e instanceof LiveConnectionRegisterError) {
        throw e;
      }
      throw new LiveConnectionRegisterError('REGISTER_FAILED', 'failed to register live connection');
    }
  }
}

/** Явное удаление (SPA-уход со страницы); отменяет onDisconnect, чтобы не было двойного remove. */
export async function removeConnection(roomId: string, connectionId: string): Promise<void> {
  const results = await Promise.allSettled([
    deleteRoomScopedPayload('timer_room_connections', roomId, connectionId).catch((err) => {
      // У viewer может не быть прав на delete; это не должно ломать отписку при уходе со страницы.
      if (isPermissionDeniedError(err)) return;
      throw err;
    }),
    untrackConnectionPresence(roomId, connectionId),
  ]);
  const rejection = results.find((r) => r.status === 'rejected');
  if (rejection) throw (rejection as PromiseRejectedResult).reason;
}

export async function updateConnectionLabel(
  roomId: string,
  connectionId: string,
  label: string
): Promise<void> {
  const normalizedLabel = normalizeConnectionLabel(label);
  const map = await fetchRoomScopedPayloadMap('timer_room_connections', roomId);
  const existing = map[connectionId];
  if (!existing || typeof existing !== 'object' || Array.isArray(existing)) return;
  await upsertRoomScopedPayload('timer_room_connections', roomId, connectionId, {
    ...(existing as Record<string, unknown>),
    label: normalizedLabel,
  });
}


export function subscribeToConnections(
  roomId: string,
  callback: (connections: Record<string, Connection>) => void
): () => void {
  let active: Record<string, Connection> | null = null;
  let generation = 0;
  const refresh = async () => {
    const currentGen = ++generation;
    try {
      const map = await fetchRoomScopedPayloadMap('timer_room_connections', roomId);
      const newActive = await getActiveConnectionsMap(roomId, map);
      if (generation === currentGen) {
        active = newActive;
        callback(active);
      }
    } catch (e) {
      console.error(e);
      if (generation === currentGen) {
        active = null;
        callback({});
      }
    }
  };
  // Heartbeat (UPDATE известной строки) применяем из события Realtime без перечитывания всего списка;
  // новые/удалённые строки и события без payload — полный refresh.
  const onChange = (event: RowChangeEvent) => {
    const row = event.eventType === 'UPDATE' ? connectionRowFromEvent(event) : null;
    const conn = row ? connectionFromRaw(row.id, row.payload) : null;
    if (row && conn && active && active[row.id]) {
      active = { ...active, [row.id]: conn };
      callback(active);
      return;
    }
    void refresh();
  };
  void refresh();
  const unsub = subscribeRoomScopedTable('timer_room_connections', roomId, onChange);
  const pollId =
    typeof window !== 'undefined'
      ? setInterval(() => void refresh(), CONNECTIONS_REFRESH_POLL_MS)
      : undefined;
  return () => {
    if (pollId !== undefined) clearInterval(pollId);
    unsub();
  };
}

export async function updateConnectionLastSeen(
  roomId: string,
  connectionId: string,
  heartbeatSecret: string
): Promise<void> {
  const at = Date.now();
  const { error: rpcError } = await supabase.rpc('timer_room_connection_touch_last_seen', {
    p_room_id: roomId,
    p_connection_id: connectionId,
    p_last_seen: at,
    p_heartbeat_secret: heartbeatSecret,
  });
  if (!rpcError) return;

  if (!isRpcUnavailableError(rpcError)) {
    try {
      const map = await fetchRoomScopedPayloadMap('timer_room_connections', roomId);
      const existing = map[connectionId];
      if (!existing || typeof existing !== 'object' || Array.isArray(existing)) return;
      await upsertRoomScopedPayload('timer_room_connections', roomId, connectionId, {
        ...(existing as Record<string, unknown>),
        lastSeen: at,
      });
    } catch (e) {
      if (isPermissionDeniedError(e)) return;
      console.warn('[connections] lastSeen upsert after RPC error', rpcError, e);
    }
    return;
  }

  const map = await fetchRoomScopedPayloadMap('timer_room_connections', roomId);
  const existing = map[connectionId];
  if (!existing || typeof existing !== 'object' || Array.isArray(existing)) return;
  await upsertRoomScopedPayload('timer_room_connections', roomId, connectionId, {
    ...(existing as Record<string, unknown>),
    lastSeen: at,
  });
}

const CONNECTION_PRESENCE_INITIAL_RETRIES = 12;
const CONNECTION_PRESENCE_INITIAL_DELAY_MS = 100;

/** `null` — не удалось проверить (сеть); не путать с «строка удалена» (`false`). */
export function subscribeToConnectionPresence(
  roomId: string,
  connectionId: string,
  callback: (exists: boolean | null) => void
): () => void {
  let cancelled = false;

  const fetchExists = async (): Promise<boolean> => {
    const map = await fetchRoomScopedPayloadMap('timer_room_connections', roomId);
    return Boolean(map[connectionId]);
  };

  const refresh = async (isInitial: boolean) => {
    if (cancelled) return;
    try {
      let exists = await fetchExists();
      // Сразу после upsert SELECT иногда не видит строку (read-after-write / реплика) — не считаем это «киком».
      if (!exists && isInitial) {
        for (let i = 0; i < CONNECTION_PRESENCE_INITIAL_RETRIES; i++) {
          if (cancelled) return;
          await new Promise((r) => setTimeout(r, CONNECTION_PRESENCE_INITIAL_DELAY_MS));
          exists = await fetchExists();
          if (exists) break;
        }
      }
      if (cancelled) return;
      callback(exists);
    } catch (e) {
      console.error(e);
      // Сетевой сбой не равен удалённой строке — иначе ложный «вас отключили».
      if (!cancelled) callback(null);
    }
  };

  void refresh(true);
  // UPDATE своей строки (heartbeat) означает, что строка существует — без лишнего запроса.
  const unsub = subscribeTableRow('timer_room_connections', 'connection_id', connectionId, (event) => {
    if (cancelled) return;
    if (event.eventType === 'UPDATE' && event.newRow) {
      callback(true);
      return;
    }
    void refresh(false);
  });
  return () => {
    cancelled = true;
    unsub();
  };
}

/** Подписка на payload конкретного подключения (для локальных эффектов UI, например title вкладки). */
export function subscribeToConnection(
  roomId: string,
  connectionId: string,
  callback: (connection: Connection | null) => void,
): () => void {
  let cancelled = false;

  const refresh = async () => {
    if (cancelled) return;
    try {
      const map = await fetchRoomScopedPayloadMap('timer_room_connections', roomId);
      if (!cancelled) callback(connectionFromRaw(connectionId, map[connectionId]));
    } catch (e) {
      console.error(e);
      if (!cancelled) callback(null);
    }
  };

  void refresh();
  const unsub = subscribeTableRow('timer_room_connections', 'connection_id', connectionId, (event) => {
    if (cancelled) return;
    const row = event.eventType === 'UPDATE' ? connectionRowFromEvent(event) : null;
    if (row && row.id === connectionId) {
      callback(connectionFromRaw(connectionId, row.payload));
      return;
    }
    void refresh();
  });
  return () => {
    cancelled = true;
    unsub();
  };
}

// ==================== Teams (timer_teams + invite link) ====================

export type { UserTeamMembership, MyTeamContext } from '../types/Team';

function teamInviteError(code: string, message?: string): Error {
  const err = new Error(message ?? code);
  (err as Error & { code?: string }).code = code;
  return err;
}

function teamIdentityCandidates(primaryUid: string, legacyUid?: string | null): string[] {
  return Array.from(
    new Set(
      [primaryUid, legacyUid].filter((x): x is string => typeof x === 'string' && x.trim() !== ''),
    ),
  );
}

/**
 * Команда текущего пользователя: владелец или участник.
 * Учитывает legacy Firebase uid (`legacyUid`), если в БД `owner_uid` / `timer_user_team_membership.uid`
 * совпадают с ним, а не с Supabase `auth.uid()`.
 */
export async function fetchMyTeam(
  authUid: string,
  legacyUid?: string | null,
): Promise<MyTeamContext | null> {
  const candidates = teamIdentityCandidates(authUid, legacyUid);

  for (const id of candidates) {
    const { data: owned, error: e1 } = await supabase
      .from('timer_teams')
      .select('*')
      .eq('owner_uid', id)
      .maybeSingle();
    if (e1) throw e1;
    if (owned) {
      const ownerUid = String(owned.owner_uid ?? '');
      return {
        role: 'owner',
        teamId: String(owned.id),
        ownerUid,
        inviteToken: String(owned.invite_token ?? ''),
        purchasedMemberSeats: Number(owned.purchased_member_seats ?? 0),
        includedMemberSeats: Number(owned.included_member_seats ?? 0),
        maxMemberSeats: Number(owned.max_member_seats ?? 4),
      };
    }
  }

  for (const id of candidates) {
    const { data: mem, error: e2 } = await supabase
      .from('timer_user_team_membership')
      .select('team_id, owner_uid')
      .eq('uid', id)
      .maybeSingle();
    if (e2) throw e2;
    if (mem?.team_id && mem.owner_uid) {
      return {
        role: 'member',
        teamId: String(mem.team_id),
        ownerUid: String(mem.owner_uid),
      };
    }
  }

  return null;
}

/** Создать команду (Premium owner). */
export async function createTeamViaRpc(): Promise<Extract<MyTeamContext, { role: 'owner' }>> {
  const { data, error } = await supabase.rpc('timer_create_team');
  if (error) throw error;
  const row = data as { team_id?: string; invite_token?: string; owner_uid?: string } | null;
  if (!row?.team_id || !row.invite_token || !row.owner_uid) {
    throw new Error('timer_create_team: unexpected response');
  }
  return {
    role: 'owner',
    teamId: row.team_id,
    ownerUid: row.owner_uid,
    inviteToken: row.invite_token,
    purchasedMemberSeats: 0,
    includedMemberSeats: 4,
    maxMemberSeats: 4,
  };
}

export async function joinTeamByInviteToken(token: string): Promise<void> {
  const { error } = await supabase.rpc('timer_join_team_by_invite', { p_token: token.trim() });
  if (error) throw error;
}

/** Индекс: участник состоит максимум в одной команде. */
export async function getUserTeamMembership(
  memberUid: string,
): Promise<UserTeamMembership | null> {
  const { data, error } = await supabase
    .from('timer_user_team_membership')
    .select('*')
    .eq('uid', memberUid)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  return {
    teamId: String(data.team_id),
    ownerUid: String(data.owner_uid ?? ''),
    addedAt: Number(data.added_at ?? 0),
  };
}

/** uid владельца тарифа комнаты: если пользователь — участник команды — uid лидера, иначе сам uid. */
export async function resolveBillingOwnerUid(uid: string, legacyUid?: string | null): Promise<string> {
  const ctx = await fetchMyTeam(uid, legacyUid);
  if (ctx?.role === 'member') return ctx.ownerUid;
  if (ctx?.role === 'owner') return ctx.ownerUid;
  return uid;
}

/** @deprecated Используйте resolveBillingOwnerUid */
export async function resolveTeamLeaderId(uid: string): Promise<string> {
  return resolveBillingOwnerUid(uid);
}

/** Подтягивает индекс membership для участников владельца команды. */
export async function syncTeamMembershipIndexesForOwner(teamOwnerUid: string): Promise<void> {
  const { data: team, error } = await supabase
    .from('timer_teams')
    .select('id')
    .eq('owner_uid', teamOwnerUid)
    .maybeSingle();
  if (error) throw error;
  if (!team?.id) return;
  const teamId = String(team.id);
  const ids = await getTeamMemberIds(teamId);
  const now = Date.now();
  for (const memberUid of ids) {
    const existing = await getUserTeamMembership(memberUid);
    if (!existing) {
      const { error: insErr } = await supabase.from('timer_user_team_membership').insert({
        uid: memberUid,
        team_id: teamId,
        owner_uid: teamOwnerUid,
        added_at: now,
      });
      if (insErr) throw insErr;
    } else if (existing.teamId !== teamId) {
      console.warn('[teams] userTeamMembership conflict', memberUid, existing.teamId, teamId);
    }
  }
}

export async function getTeamMemberIds(teamId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from('timer_team_members')
    .select('member_uid')
    .eq('team_id', teamId);
  if (error) throw error;
  return (data ?? []).map((x) => x.member_uid);
}

export function subscribeToTeamMembers(
  teamId: string,
  callback: (memberIds: string[]) => void,
): () => void {
  const refresh = async () => callback(await getTeamMemberIds(teamId));
  void refresh();
  return subscribeTableRow('timer_team_members', 'team_id', teamId, () => void refresh());
}

/** Подписка на изменения строк профиля участников/владельца (display_name после входа и т.д.). */
export function subscribeToTimerUserProfilesForUids(
  uids: string[],
  onChange: () => void,
): () => void {
  const unique = [...new Set(uids.map((u) => u?.trim()).filter(Boolean))] as string[];
  if (unique.length === 0) return () => {};
  const stoppers = unique.map((uid) => subscribeTableRow('timer_user_profiles', 'uid', uid, onChange));
  return () => {
    stoppers.forEach((stop) => stop());
  };
}

export async function addTeamMember(
  teamId: string,
  billingOwnerUid: string,
  memberUid: string,
): Promise<void> {
  const memberUidTrim = memberUid.trim();
  if (memberUidTrim === billingOwnerUid) {
    throw teamInviteError(TEAM_INVITE_ERROR.SELF_INVITE);
  }

  const existing = await getUserTeamMembership(memberUidTrim);
  if (existing && existing.teamId !== teamId) {
    throw teamInviteError(
      TEAM_INVITE_ERROR.ALREADY_IN_TEAM,
      'Пользователь уже в другой команде.',
    );
  }

  const profile = await getUserProfile(billingOwnerUid);
  const plan = (profile?.plan ?? 'free') as PlanTier;
  const maxMembers = getMaxTeamMembersForPlan(plan);
  const ids = await getTeamMemberIds(teamId);
  if (ids.includes(memberUidTrim)) return;
  if (ids.length >= maxMembers - 1) {
    throw teamInviteError(TEAM_INVITE_ERROR.TEAM_LIMIT);
  }

  const addedAt = Date.now();
  const { error: memberErr } = await supabase.from('timer_team_members').upsert(
    {
      team_id: teamId,
      member_uid: memberUidTrim,
      added_at: addedAt,
    },
    { onConflict: 'team_id,member_uid' },
  );
  if (memberErr) throw memberErr;
  const { error: membershipErr } = await supabase.from('timer_user_team_membership').upsert(
    {
      uid: memberUidTrim,
      team_id: teamId,
      owner_uid: billingOwnerUid,
      added_at: addedAt,
    },
    { onConflict: 'uid' },
  );
  if (membershipErr) throw membershipErr;
}

export async function removeTeamMember(teamId: string, memberUid: string): Promise<void> {
  const { error: memberErr } = await supabase
    .from('timer_team_members')
    .delete()
    .eq('team_id', teamId)
    .eq('member_uid', memberUid);
  if (memberErr) throw memberErr;
  const { error: membershipErr } = await supabase
    .from('timer_user_team_membership')
    .delete()
    .eq('uid', memberUid)
    .eq('team_id', teamId);
  if (membershipErr) throw membershipErr;
}
